import { queryClient, useMutation } from '../../../../ReactQuery';
import { put } from '../../../../redux/api';
import type { FullTeamGroupResponse, TeamUserAction, UpdateTeamUsersResponse } from '../../../../../shared/types/routes/teamGroups';

import { getTeamGroupQueryKey } from './useTeamGroup';

//  Function to actually add members to a team group
function addMembersToTeamGroup(teamGroupId: number, action: TeamUserAction) {
  return put<TeamUserAction, UpdateTeamUsersResponse>(`/api/teams/groups/${teamGroupId}/users`, action)
    //  Note: we are doing this so that we can pass along the response to the onSuccess callback
    .then(response => {
      if (!response.data) {
        throw new Error('Failed to add members to team group');
      }
      return response.data;
    });
}

/**
 * Mutation for adding members to a team group.
 */
export default function useAddMembersToTeamGroup(teamGroupId: number, action: TeamUserAction) {
  return useMutation({
    mutationFn: () => addMembersToTeamGroup(teamGroupId, action),
    onSuccess: (data: UpdateTeamUsersResponse) => {
      //  This is kind of janky, because the response data only includes the userId and roleId.
      //  We put this data in the query cache immediately, so that the UI updates immediately,
      //  but we also trigger a re-fetch of the team group data, which will overwrite the data in the cache
      //  with the full team group members data.
      queryClient.setQueryData(
        getTeamGroupQueryKey(teamGroupId),
        (oldData?: FullTeamGroupResponse) => {
          if (!oldData || !data) {
            return oldData;
          }
          return {
            ...oldData,
            members: [
              ...oldData.members,
              ...data.users.map(user => ({
                ...user,
                userId: user.id,
                roleId: user.roleId,
                //  Placeholders until the response comes in and updates this.
                username: '...',
                email: '...',
              })),
            ],
          };
        },
      );
      //  Trigger a re-fetch of the team group data, so that the UI updates immediately.
      queryClient.invalidateQueries({
        queryKey: getTeamGroupQueryKey(teamGroupId),
      });
    },
  });
}
